import { ensureDefined, intervalToMs } from '@avantia/client-and-server-utilities';
import { isDefined } from '@avantia/form-validation';
import { hasOwnProperty } from '@avantia/logging-and-errors';
import { HpDimensions, HpUiEventData, destructureControlId } from '@avantia/questionset-model';
import jQuery from 'jquery';
import { v4 as uuid } from 'uuid';
import {
  HpBindFieldChangeEventsProps,
  HpCustomEventHandler,
  HpGetEventFunction,
  HpTriggerFieldEventProps,
  HpTriggerGeneralActionProps,
  HpTriggerHelpInfoEventProps,
  HpTriggerLinkFollowedEventProps,
  HpTriggerModalActionProps,
  HpTriggerNavigationBarEventProps,
  HpTriggerNavigationEventProps,
  HpTriggerPageLoadEventWithCommonProps,
  HpTriggerToggleElementProps
} from '../actions/eventActionTypes';
import { bindCustomChangeEvents } from '../actions/eventActions';
import { fetchResolver } from '../actions/fetchResolver';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logInfo, logWarning } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { getHashRecord } from '../handlers/handlerUtilities';
import { getQuestionText } from '../reducers/questionSetTools';
import { HpClientQuestionData } from '../types';
import { createBulkRecordProcessor } from './bulkRecordProcessor';
import { getCookie, setCookie } from './clientCookies';
import { sendRequestOnClose } from './sendRequestOnClose';
import { getEventDetail } from './utilities';

export const KeyPrefix = 'ky';

export const eventLoggerMask = '********';

let clientDebugFlags: HpClientDebugFlags;
registerConfigurationAction((config) => {
  ({ clientDebugFlags } = config);
});

export type HpBindFieldChangeEventsPropsExtensionFunction = (
  props: HpBindFieldChangeEventsProps,
  functions: HpBindFieldChangeEventsFunctions
) => HpBindFieldChangeEventsProps;

export interface HpConfigureEventLoggerProps {
  answersKey: string;
  extend?: HpBindFieldChangeEventsPropsExtensionFunction;
  websiteEndpoint: string;
}

export interface HpBindFieldChangeEventsFunctions {
  addEventRecord: (eventData: HpUiEventData) => void;
  getCookieData: () => GetCookieDataResult;
}

export function configureEventLogger({ answersKey, extend, websiteEndpoint }: HpConfigureEventLoggerProps): void {
  const url = `${websiteEndpoint}/api/events`;

  const records = createBulkRecordProcessor<HpUiEventData>({
    minRecordsToProcess: 20,
    maxTimeBetweenProcessing: intervalToMs('15s'),
    canProcess: () => {
      return !!getAnswersId();
    },
    processor: (events, timesCalled) => {
      const answersId = ensureDefined(getAnswersId(), 'answersId');
      const eventCount = events.length;

      if (timesCalled === 1) {
        addMissingValues(events, { answersId });
      }

      return fetchResolver<HpUiEventData[], HpEventResponse>({
        options: { method: 'POST', url, data: events, description: `Save ${eventCount} events for ID ${answersId}` },
        loadingPageType: 'none',
        successAction: () => {
          logInfo(`Saved ${eventCount} events (${timesCalled})`, HpClientDebugFlags.EventProcessor);
        },
        failedAction: (reason) => {
          logWarning(
            `Failed to saved ${eventCount} events (${timesCalled}): ${reason}`,
            HpClientDebugFlags.EventProcessor
          );
        },
        action: 'FIELD_CHANGED',
        dispatch: () => Promise.resolve()
      });
    }
  });

  const onFieldChange = ({ customEvent, data }: HpCustomEventHandler<HpTriggerFieldEventProps>) => {
    const { getEvent } = data;
    const eventData = createFieldChangeData({
      ...getCookieData(),
      data,
      timeStamp: customEvent.timeStamp
    });
    logEventWithMask({
      eventData,
      getEvent,
      logEventFunction: addEventRecord,
      propertyToMask: 'value'
    });
  };

  const onToggleElement = ({ customEvent, data }: HpCustomEventHandler<HpTriggerToggleElementProps>) => {
    const { getEvent, isVisible, question, targetType } = data;
    const event = getEvent();
    const { target, eventType } = getEventDetail(event);
    const eventData: HpUiEventData = {
      ...getCookieData(),
      eventType,
      targetId: target ? target.id : '',
      targetType,
      isVisible,
      questionId: question.questionId,
      timeStamp: customEvent.timeStamp
    };

    addEventRecord(eventData);
  };

  const onGeneralAction = ({ customEvent, data }: HpCustomEventHandler<HpTriggerGeneralActionProps>) => {
    const { getEvent } = data;
    const event = getEvent();
    const { target, eventType } = getEventDetail(event);
    const eventData: HpUiEventData = {
      ...getCookieData(),
      eventType,
      targetId: target ? target.id : '',
      timeStamp: customEvent.timeStamp
    };

    addEventRecord(eventData);
  };

  const onHelpInfoAction = ({ customEvent, data }: HpCustomEventHandler<HpTriggerHelpInfoEventProps>) => {
    const { elementText, elementType, getEvent, href, isVisible, questionId, type } = data;
    const event = getEvent();
    const { target, eventType } = getEventDetail(event);
    const eventData: HpUiEventData = {
      ...getCookieData(),
      eventType,
      questionId,
      targetType: elementType,
      key: type, // We might want to use another field for this.
      targetId: target ? target.id : '',
      isVisible,
      buttonText: elementText,
      value: href || undefined,
      timeStamp: customEvent.timeStamp
    };

    addEventRecord(eventData);
  };

  const onModalAction = ({ customEvent, data }: HpCustomEventHandler<HpTriggerModalActionProps>) => {
    const { buttonType, getEvent, question } = data;
    const event = getEvent();
    const { target, eventType } = getEventDetail(event);
    const eventData: HpUiEventData = {
      ...getCookieData(),
      eventType,
      targetId: target ? target.id : '',
      targetType: buttonType,
      questionId: question.questionId,
      timeStamp: customEvent.timeStamp
    };

    addEventRecord(eventData);
  };

  const onNavigationChange = ({ customEvent, data }: HpCustomEventHandler<HpTriggerNavigationEventProps>) => {
    const { activeSection, buttonAction, getEvent, previousSection } = data;
    const { name: toSection, sectionId, title } = activeSection;
    const { name: fromSection } = previousSection || {};
    const event = getEvent();
    let eventType: string;
    if (!event.type || event.type === 'none') {
      eventType = 'navigation-change';
    } else {
      eventType = event.type;
    }

    const eventData: HpUiEventData = {
      ...getCookieData(),
      eventType,
      targetId: sectionId,
      sectionTitle: title,
      fromSection,
      toSection,
      key: buttonAction,
      timeStamp: customEvent.timeStamp
    };

    addEventRecord(eventData);
  };

  const onNavigationBarAction = ({ customEvent, data }: HpCustomEventHandler<HpTriggerNavigationBarEventProps>) => {
    const { actionType, elementType, getEvent } = data;
    const event = getEvent();
    const { target, eventType } = getEventDetail(event);
    let menuItem = '';
    const children = $(target).children();
    if (children) {
      const menuTextItems = children.children();
      for (const child of menuTextItems) {
        const text = child.textContent || (child as HTMLElement).innerText || '';
        menuItem += `${text} `;
      }

      menuItem = menuItem.trim();
    }

    const eventData: HpUiEventData = {
      ...getCookieData(),
      eventType,
      targetId: target ? target.id : '',
      targetType: elementType,
      sectionTitle: target.title || '',
      menuItem,
      actionType,
      timeStamp: customEvent.timeStamp
    };

    addEventRecord(eventData);
  };

  const onLinkFollowed = ({ customEvent, data }: HpCustomEventHandler<HpTriggerLinkFollowedEventProps>) => {
    const { getEvent, link } = data;
    const event = getEvent();
    let key: string, text: string;
    if (link.tagName === 'BUTTON') {
      ({ id: key, innerText: text } = link as HTMLButtonElement);
    } else {
      ({ href: key, text } = link as HTMLAnchorElement);
    }

    if (!text) {
      text = jQuery(link).text(); // for IE11 etc (just in case)
    }

    const eventData: HpUiEventData = {
      ...getCookieData(),
      eventType: event.type,
      targetId: link.id || key || text,
      key: key,
      buttonText: text,
      timeStamp: customEvent.timeStamp
    };

    addEventRecord(eventData);
  };

  const onPageLoad = ({ customEvent, data }: HpCustomEventHandler<HpTriggerPageLoadEventWithCommonProps>) => {
    const { hash, path } = data;
    const eventData: HpUiEventData = {
      answersId: getAnswersIdFromCookieOrHash(hash),
      sessionId: getSessionId(),
      eventType: `page-load`,
      targetId: '',
      hash: hash || '',
      key: path,
      timeStamp: customEvent.timeStamp
    };

    const { userAgent, screen, viewport } = getClientDetail();
    if (userAgent && screen && viewport) {
      eventData.userAgent = userAgent;
      eventData.screen = screen;
      eventData.viewport = viewport;
    }

    addEventRecord(eventData);
  };

  function addEventRecord(eventData: HpUiEventData) {
    const { eventType, timeStamp } = ensureDefined(eventData, 'eventData');
    ensureDefined(eventType, 'eventType');
    ensureDefined(timeStamp, 'timeStamp');
    records.add(eventData);
    if (clientDebugFlags & HpClientDebugFlags.EventLog) {
      logInfo({ name: 'event-logger', ...eventData }, HpClientDebugFlags.None);
    }
  }

  // Just in case it's not initialized at first
  function getAnswersId(): string {
    return getCookie(answersKey) || '';
  }

  function getSessionId(): string {
    const name = 'hpSessionId';
    let sessionId = getCookie(name);
    if (!sessionId) {
      sessionId = uuid();
      setCookie({ name, value: sessionId });
    }

    return sessionId || '';
  }

  function getCookieData(): GetCookieDataResult {
    const answersId = getAnswersId();
    const sessionId = getSessionId();
    return { answersId, sessionId };
  }

  function getAnswersIdFromCookieOrHash(hash: string | null): string {
    let guid: string | undefined = getAnswersId();
    if (!guid && hash) {
      ({ guid } = getHashRecord<{ guid: string }>(hash, ['guid']));
    }

    return guid || '';
  }

  let props: HpBindFieldChangeEventsProps = {
    onFieldChange,
    onToggleElement,
    onNavigationChange,
    onNavigationBarAction,
    onPageLoad,
    onGeneralAction,
    onHelpInfoAction,
    onModalAction,
    onLinkFollowed
  };

  if (extend) {
    props = extend(props, { addEventRecord, getCookieData });
  }

  bindCustomChangeEvents(props);

  sendRequestOnClose(url, (eventType) => {
    const { answersId, sessionId } = getCookieData();
    if (eventType === 'beforeunload') {
      const closeEvent: HpUiEventData = {
        answersId,
        sessionId,
        eventType: 'tab-close',
        targetId: '',
        timeStamp: new Date().getTime()
      };
      addEventRecord(closeEvent);
    }

    const data = records.removeAll();
    addMissingValues(data, { answersId });
    return data.length > 0 ? data : undefined;
  });

  interface AddMissingValuesProps {
    answersId: string;
  }

  function addMissingValues(events: HpUiEventData[], props: AddMissingValuesProps): void {
    const { answersId } = props;
    if (answersId) {
      // very occasionally, the first event doesn't contain an answersId value.
      events.forEach((event) => {
        if (!event.answersId) {
          event.answersId = answersId;
        }
      });
    }
  }
}

interface GetCookieDataResult {
  answersId: string;
  sessionId: string;
}

interface HpEventResponse {
  success: boolean;
}

export interface HpCreateFieldChangeDataProps {
  answersId: string;
  data: HpTriggerFieldEventProps;
  sessionId: string;
  timeStamp: number;
}

export function createFieldChangeData({
  answersId,
  data,
  sessionId,
  timeStamp
}: HpCreateFieldChangeDataProps): HpUiEventData {
  const { getEvent, key: dataKey, question, questionFormats, section, ui, value: dataValue } = data;
  const event = getEvent();
  const { target, targetId, eventType } = getEventDetail(event);
  const { mappingField, questionId, dataType } = question || {};
  const eventData: HpUiEventData = {
    answersId,
    sessionId,
    targetId,
    questionId,
    eventType,
    mappingField: mappingField as string,
    timeStamp
  };

  if (section && dataKey === 'nav-bar') {
    const { sectionId, title: sectionTitle } = section;
    const { textContent } = target;
    eventData.sectionId = sectionId;
    eventData.sectionTitle = sectionTitle;
    eventData.menuItem = textContent || '';
  } else if (question && dataType === 'button') {
    if (question.data) {
      const { buttonText } = getQuestionText({ question, questionUI: ui, questionFormats });
      eventData.buttonText = buttonText;
    }

    if (dataValue) {
      eventData.value = dataValue;
    }
  } else if (targetId) {
    // eslint-disable-next-line prefer-const
    let { name: inputName, tagName, textContent, title: inputTitle, type: inputType, value } = target;
    inputType = inputType ? inputType.toLowerCase() : '';
    if (tagName === 'BUTTON' && textContent) {
      eventData.buttonText = textContent;
    } else if (tagName === 'INPUT' && inputType === 'radio') {
      eventData.buttonText = inputName || inputTitle;
    }

    const { groupNumber, key: controlKey } = destructureControlId(targetId, { requirePrefix: false });
    if (groupNumber !== undefined) {
      eventData.groupNumber = groupNumber;
    }

    if (value === '' && controlKey) {
      value = controlKey;
    } else if (controlKey) {
      eventData.key = controlKey;
    }

    if (value && value.startsWith && value.startsWith(KeyPrefix) && question) {
      const lookup = (question.data as HpClientQuestionData).lookup;
      if (lookup && hasOwnProperty(lookup, value)) {
        value = value.substring(KeyPrefix.length);
      }
    }

    if (isDefined(value)) {
      eventData.value = value;
    }

    if (eventData.buttonText && eventData.buttonText === eventData.value) {
      delete eventData.buttonText;
    }
  } else if (!dataType && dataValue) {
    // Not a question and with no value in target (i.e. div).
    eventData.value = dataValue;
  }

  return eventData;
}

function getClientDetail(): { screen?: HpDimensions; viewport?: HpDimensions; userAgent?: string } {
  let win: (Window & typeof globalThis) | undefined;
  try {
    win = window;
  } catch {
    return {};
  }

  if (win) {
    const { screen, navigator } = win || {};
    const jwin = jQuery(win);
    const width = jwin.width() || 0;
    const height = jwin.height() || 0;
    return {
      userAgent: navigator.userAgent,
      screen: { width: screen.width, height: screen.height },
      viewport: { width, height }
    };
  }

  return {};
}

interface HpLogEventWithMaskProps<EventDataT extends Record<string, any>> {
  eventData: EventDataT;
  getEvent: HpGetEventFunction;
  logEventFunction: (eventData: EventDataT) => void;
  propertyToMask: keyof EventDataT;
}

export function logEventWithMask<EventDataT extends Record<string, any>>({
  eventData,
  getEvent,
  logEventFunction,
  propertyToMask
}: HpLogEventWithMaskProps<EventDataT>): void {
  const { target } = getEventDetail(getEvent());
  const { dataset } = (target as HTMLElement) || {};
  const { hpPrivacyLevel } = dataset || {};
  if (hpPrivacyLevel === 'exclude') {
    return;
  } else if (hpPrivacyLevel === 'mask') {
    eventData[propertyToMask] = eventLoggerMask as EventDataT[typeof propertyToMask];
  }

  logEventFunction(eventData);
}
